import React, {useEffect, useMemo, useRef, useState} from 'react';
import AssetsTable, {ExtendedGridColDef} from "../../components/AssetsTable";
import {GridPaginationModel} from "@mui/x-data-grid-pro";
import {SearchAssetTdas} from "../../graphql/queries/searchAssetTdas";
import {addToUrlParams} from "@dreebit-registry/vsm-microservice-components";
import _ from "lodash";
import { useSyncedListParams } from "../../hooks/useSyncedListParams";
import ListWithFilterDrawer from '../../components/ListWithFilterDrawer';
import {Chip, Typography} from "@mui/joy";
import {Button, Tooltip} from "@mui/material";
import {SearchImportAssets} from "../../graphql/queries/searchImportAssets";
import {useTranslation} from "react-i18next";
import ImportAssetApprovalModal from "../../components/ImportAssetApprovalModal";
import moment from "moment";
import {defaultQueryFields} from "../assets/assetList";
import {SearchAssetQueryField} from "../../codegen/graphql";
import {resolveValidQueryFields} from "../../utils/resolveValidQueryFields";
import {TypeDefinitionAttribute} from "../../types";

interface Props {
    onOpenAsset: (assetId: string) => void;
}

const ImportListScreen = ({onOpenAsset}: Props) => {
    const {t} = useTranslation();
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
    const [attributes, setAttributes] = useState<TypeDefinitionAttribute[]>([]);

    const [assetLocationIds, setAssetLocationIds] = useState<string[]>([]);
    const [approveModal, setApproveModal] = useState<{
        open: boolean;
        assetLocationId: string | undefined;
    }>({ open: false, assetLocationId: undefined });

    const showEnvBanner = process.env.REACT_APP_SHOW_TESTING_BANNER === "true"
    const tableHeight =
        viewportHeight
        - (showEnvBanner ? 40 : 0) // Test Env Banner
        - 61 // Filter SearchBar
        - 63 // header
        - 16; // content padding

    // Derived from this list's own search TDA (typeDefinitionIndex ASSET_LOCATION_IMPORT), whose
    // option list may legitimately differ from the asset list's. The generated enum is only the
    // bootstrap fallback until the TDAs have loaded.
    const validQueryFields = useMemo(
        () => resolveValidQueryFields(attributes, Object.values(SearchAssetQueryField)),
        [attributes]
    );

    const {
        urlParams,
        setUrlParams,
        searchParams,
        setSearchParams,
        filterParams,
        setFilterParams,
        resetFilterParams
    } = useSyncedListParams({
        storagePrefix: "importList",
        defaultQueryFields: defaultQueryFields,
        validQueryFields: validQueryFields,
    });

    // See assetList: the debounced handler must be stable, and urlParams/setUrlParams are read
    // through refs because react-router recreates both on every URL change.
    const urlParamsRef = useRef(urlParams);
    urlParamsRef.current = urlParams;
    const setUrlParamsRef = useRef(setUrlParams);
    setUrlParamsRef.current = setUrlParams;

    const onDebouncedSearch = useMemo(
        () => _.debounce((values: { query: string; fields: string[] }) => {
            setSearchParams(values);
            addToUrlParams(urlParamsRef.current, values, setUrlParamsRef.current);
        }, 700),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => () => onDebouncedSearch.cancel(), [onDebouncedSearch]);

    useEffect(() => {
        const handleResize = () => {
            setViewportHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    const customColumns: ExtendedGridColDef[] = [
        {
            field: "name",
            headerName: t("Name"),
            flex: 1,
            valueGetter: (value, row) => {
                const assetTitle = _.get(row, "attributes.title");
                const productTitle = _.get(row, "asset.product.name");

                return assetTitle || productTitle || "";
            },
            disableColumnMenu: true,
        },
        {
            field: "serialNumber",
            headerName: t("Serial No."),
            flex: 1,
            renderCell: (params) => {
                const assetLocationId = _.get(params.row, "assetLocation.id");
                const serialNumber = _.get(params.row, "asset.serialNumber");

                return (
                    <Typography
                        fontWeight={"bold"}
                        sx={{ cursor: "pointer" }}
                        color={"primary"}
                        onClick={() => onOpenAsset(assetLocationId)}
                    >
                        {serialNumber}
                    </Typography>
                );
            },
            display: "flex",
            disableColumnMenu: true,
        },
        {
            field: "product",
            headerName: t("Product"),
            flex: 1,
            valueGetter: (value, row) => {
                return _.get(row, "asset.product.name");
            },
            disableColumnMenu: true,
        },
        {
            field: "client",
            headerName: t("Company"),
            flex: 1,
            valueGetter: (value, row) => {
                return _.get(row, "assetLocation.owner.name");
            },
            disableColumnMenu: true,
        },
        {
            field: "location",
            headerName: t("Location"),
            flex: 1,
            renderCell: (params) => {
                const pathNodes = _.get(params.row, "assetLocation.installedBaseNode.pathNodes", []);
                const pathNodeTitles = pathNodes.map((p: any) => p.title)

                return <Tooltip
                    title={pathNodeTitles.join(" / ")}
                    placement={"top-start"}
                    enterDelay={700}
                    enterNextDelay={500}
                >
                    {pathNodeTitles.join(" / ")}
                </Tooltip>
            },
            disableColumnMenu: true,
        },
        {
            field: "insert",
            headerName: t("Insert Date"),
            flex: 1,
            valueGetter: (value, row) => {
                return _.get(row, "asset.assetLocationImport.insert");
            },
            renderCell: (params) => {
                const insertDate = _.get(params.row, "asset.assetLocationImport.insert");

                return moment(insertDate).format('DD.MM.YY HH:mm');
            },
            disableColumnMenu: true,
        },
        {
            field: "approval",
            headerName: t("Approval"),
            width: 140,
            align: "center",
            renderCell: (params) => {
                const approvalStatus = _.get(params.row, "asset.assetLocationImport.status");
                const assetLocationId = _.get(params.row, "assetLocation.id");

                if (approvalStatus === "OPEN") {
                    return <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        onClick={() => setApproveModal({open: true, assetLocationId: assetLocationId})}
                        size={"small"}
                    >
                        {t('Approve')}
                    </Button>
                } else if (approvalStatus === "APPROVED") {
                    return <Chip color={"neutral"}>
                        {t("Approved")}
                    </Chip>
                }
            },
            disableColumnMenu: true,
        },
    ];

    return (
        <ListWithFilterDrawer
            query={SearchAssetTdas}
            attributesPath="searchAssets.filterTypeDefinitionAttributeList.typeDefinitionAttributes"
            queryVariables={{typeDefinitionIndex: "ASSET_LOCATION_IMPORT"}}
            urlParams={urlParams}
            setUrlParams={setUrlParams}
            searchParams={searchParams}
            filterParams={filterParams}
            setFilterParams={setFilterParams}
            resetFilterParams={resetFilterParams}
            onSearch={onDebouncedSearch}
            onAttributesChange={setAttributes}
        >
            <AssetsTable
                height={tableHeight}
                searchParams={searchParams}
                urlParams={urlParams}
                filter={filterParams}
                staticFilters={{
                    assetLocationImportStatus: { equals: "OPEN" }
                }}
                onChangePagination={(newPaginationModel: GridPaginationModel) => {
                    if (newPaginationModel) {
                        addToUrlParams(urlParams, newPaginationModel, setUrlParams);
                    }
                }}
                onOpenAsset={onOpenAsset}
                columns={customColumns}
                query={SearchImportAssets}
                onDataLoaded={setAssetLocationIds}
            />
            {approveModal.open && approveModal.assetLocationId && (
                <ImportAssetApprovalModal
                    assetLocationId={approveModal.assetLocationId}
                    open={approveModal.open}
                    onClose={() => setApproveModal({open: false, assetLocationId: undefined})}
                    allAssetLocationIds={assetLocationIds}
                    onAssetChange={(nextId: string) =>
                        setApproveModal({ open: true, assetLocationId: nextId })
                    }
                />
            )}
        </ListWithFilterDrawer>
    );
};

export default ImportListScreen;


